import React from 'react';
import { Card, Badge, Button } from 'react-bootstrap';

const TemplateCard = ({ template, onTestMessage, onEdit }) => {
  // OTA-channel templates (ota_checkin_instructions, ota_checkout_reminder, …) are driven by their
  // own standalone crons, NOT the paused legacy scheduler — so template.stats and the test-send
  // button don't apply to them. Derived from the channel flag rather than a per-templateId check so
  // every future OTA template is covered automatically.
  const isOtaTemplate = !!template.channels?.ota?.enabled;

  const getCategoryBadge = () => {
    if (template.category === 'guest') {
      return <Badge bg="info">رسالة ضيف</Badge>;
    } else if (template.category === 'staff') {
      return <Badge bg="secondary">رسالة موظف</Badge>;
    }
    return null;
  };

  const getChannelIcons = () => {
    const icons = [];
    if (template.channels?.whatsapp?.enabled) {
      icons.push('📱 واتساب');
    }
    if (template.channels?.sms?.enabled) {
      icons.push('💬 SMS');
    }
    if (isOtaTemplate) {
      icons.push('🌐 محادثة المنصة');
    }
    return icons.join(' • ');
  };

  const stats = template.stats || { sentCount: 0, deliveredCount: 0, failedCount: 0 };

  return (
    <Card className="h-100 border-0 shadow-sm" dir="rtl">
      <Card.Body>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="flex-grow-1">
            <h5 className="mb-2">{template.name?.ar || 'غير محدد'}</h5>
            <div className="d-flex gap-2 align-items-center">
              {getCategoryBadge()}
              {template.active ? (
                <Badge bg="success">نشط</Badge>
              ) : (
                <Badge bg="secondary">غير نشط</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {template.description?.ar && (
          <p className="text-muted small mb-3">
            {template.description.ar}
          </p>
        )}

        {/* Channels */}
        <div className="mb-3">
          <small className="text-muted">القنوات:</small>
          <div>{getChannelIcons()}</div>
        </div>

        {/* Stats — hidden for OTA templates: their sends are recorded in the booking ledger
            (checkin_message_sends), not template.stats, so these would read 0/0/0 forever
            (tasks/checkin-message-automation.md). Real per-booking status lives on the booking page. */}
        {!isOtaTemplate && (
          <div className="mb-3">
            <small className="text-muted">الإحصائيات:</small>
            <div className="d-flex justify-content-between mt-1">
              <div className="text-center">
                <div className="fw-bold">{stats.sentCount.toLocaleString('ar-SA')}</div>
                <small className="text-muted">مرسل</small>
              </div>
              <div className="text-center">
                <div className="fw-bold text-success">{stats.deliveredCount.toLocaleString('ar-SA')}</div>
                <small className="text-muted">وصل</small>
              </div>
              <div className="text-center">
                <div className="fw-bold text-danger">{stats.failedCount.toLocaleString('ar-SA')}</div>
                <small className="text-muted">فشل</small>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        {/* Test-send routes through the paused legacy scheduler (WhatsApp/SMS), not the standalone
            OTA crons — hide it for OTA templates so ops don't rely on a no-op
            (tasks/checkin-message-automation.md · tasks/checkout-message-automation.md). */}
        {template.active && !isOtaTemplate && (
          <Button
            variant="outline-primary"
            size="sm"
            className="w-100"
            onClick={() => onTestMessage(template)}
          >
            إرسال رسالة تجريبية
          </Button>
        )}
        <Button
          variant="outline-secondary"
          size="sm"
          className="w-100 mt-2"
          onClick={() => onEdit(template)}
        >
          تعديل القالب
        </Button>
      </Card.Body>
    </Card>
  );
};

export default TemplateCard;
